import { ReactNode, useEffect, useRef, useState } from "react";
import FeedbackWidget from "./FeedbackWidget";
import HeroCard, { HeroProps } from "./HeroCard";
import LensTabs from "./LensTabs";
import PhotoCredits from "./PhotoCredits";
import PilotChip from "./PilotChip";

export type AppRoute = "dashboard" | "stats" | "high_scores" | "proficiencies" | "feed" | "pilots" | "achievements" | "greenie_board" | "admin";

interface PageLayoutProps {
  route: AppRoute;
  wide?: boolean;
  hero?: HeroProps;
  children: ReactNode;
}

const NAV_LINKS = [
  { route: "dashboard", label: "Dashboard", href: "#/dashboard" },
  { route: "proficiencies", label: "Proficiencies", href: "#/proficiencies" },
  { route: "achievements", label: "Achievements", href: "#/achievements" },
  { route: "high_scores", label: "High Scores", href: "#/high_scores" },
  { route: "stats", label: "Stats", href: "#/stats" },
  { route: "greenie_board", label: "Greenie Board", href: "#/greenie_board" },
  { route: "feed",      label: "Feed",      href: "#/feed" },
  { route: "pilots",    label: "Pilots",    href: "#/pilots" },
] as const;

const PageLayout = ({ route, wide = false, hero, children }: PageLayoutProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [menuOpen]);

  // Close dropdown when hash changes (link was followed)
  useEffect(() => {
    const close = () => setMenuOpen(false);
    window.addEventListener("hashchange", close);
    return () => window.removeEventListener("hashchange", close);
  }, []);

  return (
    <main className={`page${wide ? " page-wide" : ""}`}>
      <header className="page-header" ref={headerRef}>
        <div className="page-header-row">
          <h1 className="page-header-title">
            <img className="page-header-logo" src="/checkride-gold-slim.png" alt="Checkride" />
            <span className="beta-tag">BETA</span>
          </h1>

          <div className="page-header-actions">
            {route !== "admin" && <FeedbackWidget />}
            {route !== "admin" && <PilotChip />}

            {route !== "admin" && (
              <button
                className={"hamburger-btn" + (menuOpen ? " is-open" : "")}
                aria-label={menuOpen ? "Close navigation" : "Open navigation"}
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((v) => !v)}
              >
                <span className="hamburger-bar" />
                <span className="hamburger-bar" />
                <span className="hamburger-bar" />
              </button>
            )}
          </div>
        </div>

        {route !== "admin" && (
          <>
            <nav className="page-nav" aria-label="Primary">
              {NAV_LINKS.map(({ route: r, label, href }) => (
                <a
                  key={r}
                  className={"page-nav-link" + (route === r ? " is-active" : "")}
                  aria-current={route === r ? "page" : undefined}
                  href={href}
                >
                  {label}
                </a>
              ))}
            </nav>

            {menuOpen && (
              <nav className="page-nav-dropdown" aria-label="Primary mobile">
                {NAV_LINKS.map(({ route: r, label, href }) => (
                  <a
                    key={r}
                    className={"page-nav-dropdown-link" + (route === r ? " is-active" : "")}
                    aria-current={route === r ? "page" : undefined}
                  href={href}
                    onClick={() => setMenuOpen(false)}
                  >
                    {label}
                  </a>
                ))}
                <div className="page-nav-dropdown-feedback">
                  <FeedbackWidget inline />
                </div>
              </nav>
            )}
          </>
        )}
      </header>

      <div className="page-shell">
        {hero && <HeroCard {...hero} size={hero.size ?? "md"} className={hero.className ? `page-hero ${hero.className}` : "page-hero"} />}
        {hero && route !== "admin" && route !== "pilots" && <LensTabs />}
        <div className="page-content">{children}</div>
      </div>
      <PhotoCredits />
    </main>
  );
};

export default PageLayout;

